import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function ReceivingForm() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    dc_number: "",
    dc_date: new Date().toISOString().slice(0, 10),
    customer_id: "",
    plant_id: "",
    department: "",
    contact_person: "",
    contact_phone: "",
    transporter: "",
    vehicle_number: "",
    num_cylinders_received: 1,
    remarks: "",
  });

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)); }, []);
  useEffect(() => {
    if (!form.customer_id) { setPlants([]); return; }
    api.get(`/plants?customer_id=${form.customer_id}`).then((r) => setPlants(r.data));
  }, [form.customer_id]);

  async function save() {
    if (!form.dc_number.trim()) { toast.error("DC Number is required"); return; }
    if (!form.customer_id) { toast.error("Customer is required"); return; }
    if (!form.num_cylinders_received || form.num_cylinders_received < 1) { toast.error("At least 1 cylinder"); return; }
    setSaving(true);
    try {
      const body = { ...form, plant_id: form.plant_id || null };
      const { data } = await api.post("/receiving", body);
      toast.success(`DC ${data.dc_number} created`);
      nav(`/receiving/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create");
    } finally { setSaving(false); }
  }

  return (
    <div data-testid="receiving-form-page" className="space-y-4 max-w-4xl">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <button onClick={() => nav("/receiving")} className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to Receiving
        </button>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">
          Reconditioning · Cylinder Received First
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
          New Receiving Entry
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Log a delivery challan for cylinders received for reconditioning. No cylinder numbers yet — they're generated after OA release.
        </p>
      </div>

      <Card className="border-slate-200 p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              DC Number * <span className="text-slate-400 normal-case font-normal">(customer-provided)</span>
            </Label>
            <Input data-testid="rec-dc-number" className="mt-1 font-mono" value={form.dc_number}
                   onChange={(e) => setForm({ ...form, dc_number: e.target.value })}
                   placeholder="e.g. JSW/DC/2026-07/00123" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">DC Date *</Label>
            <Input type="date" data-testid="rec-dc-date" className="mt-1 font-mono" value={form.dc_date}
                   onChange={(e) => setForm({ ...form, dc_date: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Customer *</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, plant_id: "" })}>
              <SelectTrigger data-testid="rec-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Plant</Label>
            <Select value={form.plant_id || "none"} onValueChange={(v) => setForm({ ...form, plant_id: v === "none" ? "" : v })} disabled={!form.customer_id}>
              <SelectTrigger data-testid="rec-plant"><SelectValue placeholder="Select plant (optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— none —</SelectItem>
                {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Department</Label>
            <Input data-testid="rec-department" className="mt-1" value={form.department}
                   onChange={(e) => setForm({ ...form, department: e.target.value })}
                   placeholder="e.g. Maintenance / Utilities" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Contact Person</Label>
            <Input data-testid="rec-contact-person" className="mt-1" value={form.contact_person}
                   onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Contact Phone</Label>
            <Input data-testid="rec-contact-phone" className="mt-1 font-mono" value={form.contact_phone}
                   onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Transporter</Label>
            <Input data-testid="rec-transporter" className="mt-1" value={form.transporter}
                   onChange={(e) => setForm({ ...form, transporter: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vehicle Number</Label>
            <Input data-testid="rec-vehicle" className="mt-1 font-mono" value={form.vehicle_number}
                   onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })}
                   placeholder="Optional" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Number of Cylinders Received *</Label>
            <Input type="number" min={1} data-testid="rec-num-cylinders" className="mt-1 font-mono"
                   value={form.num_cylinders_received}
                   onChange={(e) => setForm({ ...form, num_cylinders_received: parseInt(e.target.value, 10) || 1 })} />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Remarks</Label>
          <Textarea data-testid="rec-remarks" rows={3} className="mt-1"
                    value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                    placeholder="Symptoms, observed damage, urgency, etc." />
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => nav("/receiving")}>Cancel</Button>
        <Button data-testid="rec-create-btn" disabled={saving} onClick={save}
                className="bg-red-700 hover:bg-red-800 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Receiving Entry"}
        </Button>
      </div>
    </div>
  );
}
